<script setup>
  import { ref, watch } from 'vue';
  import BaseModal from '@/components/base/BaseModal.vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  const showModal = defineModel();

  const props = defineProps({
    downloadInfo: {
      type: Object,
      required: true
    }
  });

  const blobUrl = ref(null);

  watch(showModal, async (open) => {
    if (!open) {
      if (blobUrl.value) {
        URL.revokeObjectURL(blobUrl.value);
        blobUrl.value = null;
      }
      return;
    }
    const response = await fetch(props.downloadInfo.url);
    const blob = await response.blob();
    blobUrl.value = URL.createObjectURL(blob);
  });

  function handleDownloadData() {
    // Defer closing the modal so the browser can start reading the blob
    // before the watcher revokes its object URL. Revoking synchronously here
    // kills the download mid-flight and Chrome reports it as a network error.
    setTimeout(() => {
      showModal.value = false;
    }, 0);
  }
</script>

<template>

  <BaseModal
    v-model="showModal"
  >
    <header class="jwx-modal__header">
      <h2 class="jwx-modal__title">{{ t('containers.archive.downloadTitle') }}</h2>
    </header>          
    <div class="jwx-modal__body">
      <div class="jwx-modal__section">
        <div class="jwx-page-block jwx-meta-data__list-block-body">
          <ul class="jwx-meta-data__list">
            <li class="jwx-meta-data__list-item">
              <span class="jwx-meta-data__list-item-title">{{ t('containers.archive.startDate') }}</span>
              <div class="jwx-meta-data_list-item-body">
                <span class="jwx-meta-data__list-item-value">{{ props.downloadInfo.startDate }}</span>
              </div>
            </li>
            <li class="jwx-meta-data__list-item">
              <span class="jwx-meta-data__list-item-title">{{ t('containers.archive.endDate') }}</span>
              <div class="jwx-meta-data_list-item-body">
                <span class="jwx-meta-data__list-item-value">{{ props.downloadInfo.endDate }}</span>
              </div>
            </li>
            <li class="jwx-meta-data__list-item">
              <span class="jwx-meta-data__list-item-title">{{ t('containers.archive.fileType') }}</span>
              <div class="jwx-meta-data_list-item-body">
                <span class="jwx-meta-data__list-item-value">{{ props.downloadInfo.type.toUpperCase() }}</span>
              </div>
            </li>
            <li class="jwx-meta-data__list-item" data-direction="column">
              <span class="jwx-meta-data__list-item-title">{{ t('containers.archive.fields') }}</span>
              <div class="jwx-meta-data__list-item-body" data-full-width>
                <span class="jwx-meta-data__list-item-value">
                  {{ t('containers.archive.fieldsDescription') }}
                </span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="jwx-modal__footer">
      <div class="jwx-modal__actions">
        <button 
          type="button" 
          @click.prevent="showModal = false;"
          class="jwx-button"
        >
          {{ t('common.cancel') }}
        </button>
        <a
          :href="blobUrl"
          :download="props.downloadInfo.filename || 'archive.csv'"
          @click="handleDownloadData"
          class="jwx-button jwx-button--success"
          :class="{ 'jwx-button--disabled': !blobUrl }"
        >
          {{ t('containers.archive.download') }}
        </a>
      </div>
    </div>                  
  </BaseModal>  
</template>